import api from './base/BaseApi';
import { handleApiError } from './base/handleApiError';
import {
  ScheduledWalletTransferIntervalEnum,
  ScheduledWalletTransferStateEnum,
  TRqCreateScheduledWalletTransfer,
  TRsScheduledWalletTransfer
} from './types/ScheduledWalletTransfer';
import {
  ScheduledBankPaymentGroupStateEnum,
  ScheduledPaymentIntervalTypeEnum,
  TScheduledPaymentIntervalType
} from '@/features/payments/types/BatchPayment';
import {
  PaymentRowKindEnum,
  TPaymentListItem
} from '@/features/payments/manage/types/ManagePayments';

export const createScheduledWalletTransfer = async (
  requestBody: TRqCreateScheduledWalletTransfer
) =>
  api
    .post<TRsScheduledWalletTransfer, TRqCreateScheduledWalletTransfer>(
      `api/v1/scheduled-transfers`,
      requestBody
    )
    .then((response) => response.data)
    .catch(handleApiError);

// Maps the transfer's own (much smaller) state enum onto the shared group lifecycle state every
// Manage Payment row is tagged with (see TPaymentListItem.state) - a transfer's "scheduled" state
// reuses APPROVED, same as an individual scheduled payment that's never been attempted yet (see
// services/scheduledPayment.ts), so it renders as "Scheduled" via the existing
// isScheduledWithoutMultiSign display rule (a scheduled transfer never carries a
// multiSignActionId - see TRsScheduledWalletTransfer) rather than needing a rule of its own.
//
// COMPLETED also maps to APPROVED, not to ScheduledBankPaymentGroupStateEnum.COMPLETED:
// `transfer.state` only reflects the outcome of the *most recent* run, not whether the schedule
// itself is finished - a recurring transfer (paymentIntervalType other than ONE_OFF) whose last
// run succeeded is still upcoming and belongs in the Scheduled tab, same as one that hasn't run
// yet. Unlike individual scheduled payments (services/scheduledPayment.ts), the backend never
// deactivates a completed ONE_OFF transfer (confirmed against
// ExecuteScheduledWalletTransferUseCase - a one-off's completion is only tracked via its
// execution history, isActive is never touched), so a stale completed one-off has to be filtered
// out client-side instead - see isStaleCompletedOneOffTransfer below.
const STATE_MAP: Record<ScheduledWalletTransferStateEnum, ScheduledBankPaymentGroupStateEnum> = {
  [ScheduledWalletTransferStateEnum.SCHEDULED]: ScheduledBankPaymentGroupStateEnum.APPROVED,
  [ScheduledWalletTransferStateEnum.FAILED]: ScheduledBankPaymentGroupStateEnum.FAILED,
  [ScheduledWalletTransferStateEnum.COMPLETED]: ScheduledBankPaymentGroupStateEnum.APPROVED
};

// A ONE_OFF transfer that has already completed has nothing left to schedule and needs no
// action - unlike a recurring transfer's COMPLETED (still upcoming), this one is genuinely
// finished. The backend has no "Batch history" equivalent for wallet transfers and never
// deactivates it (see STATE_MAP above), so the only correct place for it is out of the list
// entirely, not any of the three Manage Payment tabs.
const isStaleCompletedOneOffTransfer = (transfer: TRsScheduledWalletTransfer) =>
  transfer.state === ScheduledWalletTransferStateEnum.COMPLETED &&
  transfer.paymentIntervalType === ScheduledWalletTransferIntervalEnum.ONE_OFF;

// ScheduledWalletTransferIntervalEnum and ScheduledPaymentIntervalTypeEnum carry identical wire
// values (both mirror the backend's shared ScheduledPaymentIntervalType) but are distinct
// TypeScript enums, so they aren't mutually assignable - this is an explicit, exhaustive
// (Record-typed) translation rather than a cast, so a new interval value fails to compile here
// until it's accounted for on both sides.
const INTERVAL_TYPE_MAP: Record<
  ScheduledWalletTransferIntervalEnum,
  TScheduledPaymentIntervalType
> = {
  [ScheduledWalletTransferIntervalEnum.ONE_OFF]: ScheduledPaymentIntervalTypeEnum.ONE_OFF,
  [ScheduledWalletTransferIntervalEnum.EVERY_NUMBER_OF_DAYS]:
    ScheduledPaymentIntervalTypeEnum.EVERY_NUMBER_OF_DAYS,
  [ScheduledWalletTransferIntervalEnum.EVERY_MONTH_ON_DAY]:
    ScheduledPaymentIntervalTypeEnum.EVERY_MONTH_ON_DAY,
  [ScheduledWalletTransferIntervalEnum.LAST_DAY_OF_MONTH]:
    ScheduledPaymentIntervalTypeEnum.LAST_DAY_OF_MONTH
};

// v2, not v1: `name` can't be resolved here (the backend only returns `toWalletId`, not a display
// name) - left blank and patched in by useManagePayment's rowsByGroup once the wallet list is
// available, the same deferred lookup `payFromAccount` already relies on for `walletId`.
const transformScheduledWalletTransfer = (
  transfer: TRsScheduledWalletTransfer
): TPaymentListItem => ({
  id: transfer.id,
  name: '',
  kind: PaymentRowKindEnum.SCHEDULED_TRANSFER,
  toWalletId: transfer.toWalletId,
  // Drives the Scheduled date column's recurrence annotation (see buildRecurringDateText) - a
  // scheduled transfer's Type column cell is always the literal "Scheduled transfer" regardless
  // of these (see PaymentsTable), so this pair only matters for the date column here.
  paymentIntervalType: INTERVAL_TYPE_MAP[transfer.paymentIntervalType],
  paymentIntervalValue: transfer.paymentIntervalValue,
  createdDate: transfer.createdAt,
  scheduledDate: transfer.nextTransferDate,
  totalAmount: transfer.amount,
  walletId: transfer.fromWalletId,
  state: STATE_MAP[transfer.state],
  totalPayments: 1,
  createdByCustomerId: transfer.customerId,
  note: transfer.note ?? null
});

/**
 * Active (non-deactivated) scheduled wallet transfers for an account, surfaced in Manage Payment
 * alongside scheduled payments and batches (see fetchPaymentsByGroup) - both "not yet attempted"
 * and "last run completed but still recurring" transfers land in the Scheduled tab (see STATE_MAP
 * above), "failed" ones in Action required. `active=true` restricts the response to transfers the
 * backend still considers live, but that alone isn't enough to exclude a completed one-off
 * transfer (the backend never flips isActive false for those - see
 * isStaleCompletedOneOffTransfer), so that specific case is filtered out here instead.
 *
 * Callers pass both accountId and businessId unconditionally (whatever useAuth currently has) -
 * businessId takes priority when present since accountId only applies to a personal account; the
 * caller doesn't need to know which one is relevant for the current viewer.
 */
export const getActiveScheduledWalletTransfers = async (
  accountId?: string,
  businessId?: string
): Promise<TPaymentListItem[]> => {
  const params = new URLSearchParams({ active: 'true' });
  if (businessId) {
    params.append('businessId', businessId);
  } else if (accountId) {
    params.append('accountId', accountId);
  }
  const responseBody = await api
    .get<TRsScheduledWalletTransfer[]>(`api/v1/scheduled-transfers?${params.toString()}`)
    .catch(handleApiError);

  return responseBody.data
    .filter((transfer) => !isStaleCompletedOneOffTransfer(transfer))
    .map(transformScheduledWalletTransfer);
};

/** Deactivates (deletes) a scheduled wallet transfer. Returns no body, same as deleteScheduledPayment. */
export const deactivateScheduledWalletTransfer = (id: string) =>
  api.delete<unknown, undefined>(`api/v1/scheduled-transfers/${id}`).catch(handleApiError);

/** Retries a failed scheduled wallet transfer. Returns no body - the caller can't know the new outcome. */
export const retryScheduledWalletTransfer = (id: string) =>
  api.post<unknown, null>(`api/v1/scheduled-transfers/${id}/retry`, null).catch(handleApiError);
